import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Form } from 'antd';
import { uuid } from 'short-uuid';
import styled from 'styled-components';

import {
  convertAccommodationAreasByUnitSystem,
  convertAccommodationAreasToBaseUnitSystem,
  getApartmentTypeGroupSlug,
  getContractsSlug,
} from './helpers';
import ShopDataModalAccommodationsAddRoomTypeModal from './ShopDataModalAccommodationsAddRoomTypeModal';
import ShopDataModalAccommodationsApartmentTypeGroupContent from './ShopDataModalAccommodationsApartmentTypeGroupContent';
import { useContractContext } from '../../../common/Contract/ContractContext';
import { useAppSelector } from '../../../store/hook';
import { ApartmentTypeGroup } from '../../../types/accommodations';
import GradientDivider from '../../lib/GradientDivider';
import ShopDataModalAddButton from '../common/ShopDataModalAddButton';
import ShopDataModalCategoryHeader from '../common/ShopDataModalCategoryHeader';
import { FIELD_CATEGORY } from '../constants';
import ShopDataModalAddContracts from '../contracts/ShopDataModalAddContracts';
import ShopDataModalContracts from '../contracts/ShopDataModalContracts';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Actions = styled.div`
  display: flex;
  gap: 15px;
  flex-direction: column;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: end;
  padding: 5px 20px 20px 20px;
`;

interface Props {
  actionsRef: HTMLElement | null;
}

const ShopDataModalAccommodations = ({ actionsRef }: Props) => {
  const { apartmentTypeGroups } = useAppSelector((state) => state.global);

  const {
    form,
    currentLivingType,
    currentData,
    community,
    unitSource,
    disabled,
    isCommunityCcrc,
    setCurrentData,
    handleDataStatusChanged,
  } = useShopDataModalContext();
  const {
    contracts,
    keys: existingKeys,
    selectedKey,
    setKeys,
    setSelectedKey,
    handleAvailableContracts,
  } = useContractContext();

  const [roomTypeModalOpen, setRoomTypeModalOpen] = useState(false);
  const [key, setKey] = useState<any>(uuid());
  const prevLivingTypeRef = useRef<typeof currentLivingType | null>(null);
  const currentDataRef = useRef<any>(currentData);

  currentDataRef.current = currentData;

  const handleClose = () => {
    setRoomTypeModalOpen(false);
    setKey(uuid());
  };

  const handleValuesChange = (values: any, allValues: any) => {
    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);

    const livingTypeKey = String(currentLivingType?.abv);
    const accommodations = convertAccommodationAreasToBaseUnitSystem(
      allValues[FIELD_CATEGORY.ACCOMMODATIONS],
      unitSource
    );

    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.ACCOMMODATIONS]: {
        ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
        [livingTypeKey]: {
          ...(prevState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey] || []),
          [selectedKey as string]: {
            ...(prevState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey]?.[selectedKey as string] || []),
            ...accommodations,
          },
        },
      },
    }));
  };

  useEffect(() => {
    const livingTypeKey = String(currentLivingType?.abv);
    const currentAccommodationLivingTypeData =
      currentDataRef.current[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey] ?? {};
    const availableContractKeys = Object.keys(currentAccommodationLivingTypeData);

    const prevLivingType = prevLivingTypeRef.current;
    const livingTypeChanged = prevLivingType?.abv !== currentLivingType?.abv;

    if (availableContractKeys) {
      handleAvailableContracts(availableContractKeys, livingTypeChanged ? undefined : selectedKey);
    }

    if (selectedKey) {
      const currentAccommodationLivingTypeContractData = currentAccommodationLivingTypeData[selectedKey];
      form?.setFieldValue(
        [FIELD_CATEGORY.ACCOMMODATIONS],
        convertAccommodationAreasByUnitSystem(currentAccommodationLivingTypeContractData, unitSource)
      );
    }

    prevLivingTypeRef.current = currentLivingType;
  }, [key, currentLivingType, selectedKey, community, unitSource]);

  const onSaveNewContractTabs = (selection: any[]) => {
    const livingTypeKey = currentLivingType?.abv;
    const currentLivingTypeData = currentData[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey] ?? {};

    const newKey = selection.join('|');
    currentLivingTypeData[newKey] = {};

    const newKeys = Object.keys(currentLivingTypeData);
    setKeys(newKeys);
    setSelectedKey(newKeys[0]);
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        [FIELD_CATEGORY.ACCOMMODATIONS]: {
          ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
          [livingTypeKey]: {
            ...currentLivingTypeData,
          },
        },
      };
    });
    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);
  };

  const onContractTabEdit = (selection: string[]) => {
    const livingTypeKey = currentLivingType?.abv;

    if (selection.length) {
      const selectedContracts = community?.contracts.filter((c) => selection.includes(c.slug)) || [];
      const newKey = getContractsSlug(selectedContracts);
      const newKeys = existingKeys.filter((k) => k !== selectedKey);
      newKeys.push(newKey);

      setCurrentData((prevState: any) => {
        const updatedState = {
          ...prevState,
          [FIELD_CATEGORY.ACCOMMODATIONS]: {
            ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
            [livingTypeKey]: {
              ...(prevState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey] || []),
              [newKey]: prevState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey][selectedKey as string],
            },
          },
        };
        delete updatedState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey][selectedKey as string];
        return updatedState;
      });

      setKeys(newKeys);
      setSelectedKey(newKey);
    } else {
      setCurrentData((prevState: any) => {
        const updatedState = {
          ...prevState,
          [FIELD_CATEGORY.ACCOMMODATIONS]: {
            ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
            [livingTypeKey]: {
              ...(prevState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey] || []),
            },
          },
        };
        delete updatedState[FIELD_CATEGORY.ACCOMMODATIONS][livingTypeKey][selectedKey as string];
        return updatedState;
      });
      const updatedKeys = existingKeys.filter((k) => k !== selectedKey);
      setKeys(updatedKeys);
      setSelectedKey(undefined);
    }

    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);
  };

  return (
    <Container key={key}>
      <Form style={{ position: 'relative' }} form={form} disabled={disabled} onValuesChange={handleValuesChange}>
        {roomTypeModalOpen && (
          <ShopDataModalAccommodationsAddRoomTypeModal open={roomTypeModalOpen} onClose={handleClose} />
        )}
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const currentAccommodationsData = getFieldValue(FIELD_CATEGORY.ACCOMMODATIONS);
            const currentAccommodationsApartmentTypeGroupsIds = Object.keys(currentAccommodationsData ?? {});
            const currentAccommodationsApartmentTypeGroups = apartmentTypeGroups.filter((item: ApartmentTypeGroup) =>
              currentAccommodationsApartmentTypeGroupsIds.includes(getApartmentTypeGroupSlug(item.id))
            );

            const hasContracts = Boolean(contracts.length);
            const showAddRoomTypeButton =
              apartmentTypeGroups.some(
                (item: ApartmentTypeGroup) =>
                  !currentAccommodationsApartmentTypeGroupsIds.includes(getApartmentTypeGroupSlug(item.id))
              ) &&
              (!isCommunityCcrc || hasContracts);

            const showMainCategoryBorder =
              hasContracts &&
              !apartmentTypeGroups.some((item: ApartmentTypeGroup) =>
                currentAccommodationsApartmentTypeGroupsIds.includes(getApartmentTypeGroupSlug(item.id))
              ) &&
              isCommunityCcrc;

            if (!hasContracts && isCommunityCcrc) {
              return <ShopDataModalAddContracts categories={[FIELD_CATEGORY.ACCOMMODATIONS]} />;
            }

            return (
              <>
                <HeaderContainer>
                  <ShopDataModalCategoryHeader borderless={hasContracts} label={'Accommodation'} />
                  {isCommunityCcrc && (
                    <ShopDataModalContracts
                      onEditContractTab={onContractTabEdit}
                      onSaveNewContractTabs={onSaveNewContractTabs}
                    />
                  )}
                  {showMainCategoryBorder && <div style={{ borderBottom: '1px solid var(--line-gray)' }} />}
                </HeaderContainer>
                {currentAccommodationsApartmentTypeGroups.map((group: ApartmentTypeGroup) => (
                  <ShopDataModalAccommodationsApartmentTypeGroupContent
                    key={group.id}
                    group={group}
                    onChange={handleClose}
                  />
                ))}
                {showAddRoomTypeButton &&
                  actionsRef &&
                  createPortal(
                    <Actions>
                      <GradientDivider className={'flat'} />
                      <ActionsContainer>
                        <ShopDataModalAddButton
                          label={'Add Room Type'}
                          onClick={() => setRoomTypeModalOpen(true)}
                          dataTestId="sd-modal-add-room-type-btn"
                        />
                      </ActionsContainer>
                    </Actions>,
                    actionsRef
                  )}
              </>
            );
          }}
        </Form.Item>
      </Form>
    </Container>
  );
};

export default ShopDataModalAccommodations;
